import React, { useCallback, useEffect, useState, useRef } from 'react';
import '@xyflow/react/dist/style.css';
import { EdgeProps, getBezierPath } from '@xyflow/react';
interface DotPosition {
  x: number;
  y: number;
}
export default function AnimatedEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data
}: EdgeProps) {
  const [edgePath] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetPosition,
    targetX,
    targetY
  });
  const dotCount = (data as any)?.intensity ?? 2;
  const dimmed = (data as any)?.dimmed === true;
  const dotColor = (data as any)?.dotColor ?? '#8eff01';
  const duration = 2.5; // seconds per full loop
  const pathRef = useRef<SVGPathElement>(null);
  const animRef = useRef<number>(0);
  const [dotPositions, setDotPositions] = useState<DotPosition[]>([]);
  const animate = useCallback(() => {
    const pathEl = pathRef.current;
    if (!pathEl || dimmed) return;
    const totalLength = pathEl.getTotalLength();
    if (totalLength === 0) return;
    const now = performance.now() / 1000; // seconds
    const positions: DotPosition[] = [];
    for (let i = 0; i < dotCount; i++) {
      const offset = i / dotCount;
      const t = (now / duration + offset) % 1;
      const point = pathEl.getPointAtLength(t * totalLength);
      positions.push({
        x: point.x,
        y: point.y
      });
    }
    setDotPositions(positions);
    animRef.current = requestAnimationFrame(animate);
  }, [dotCount, duration, dimmed]);
  useEffect(() => {
    if (dimmed) {
      setDotPositions([]);
      return;
    }
    // Small delay to ensure path element is rendered
    const timer = setTimeout(() => {
      animRef.current = requestAnimationFrame(animate);
    }, 50);
    return () => {
      clearTimeout(timer);
      if (animRef.current) {
        cancelAnimationFrame(animRef.current);
      }
    };
  }, [animate, dimmed, edgePath]);
  if (dimmed) {
    return <path d={edgePath} fill="none" stroke="rgba(0, 0, 0, 0.05)" strokeWidth={1} className="transition-all duration-300" data-id="element-80" />;
  }
  return <g data-id="element-81">
      <path ref={pathRef} d={edgePath} fill="none" stroke="rgba(0, 0, 0, 0.20)" strokeWidth={1.5} className="transition-all duration-300" data-id="element-82" />
      {dotPositions.map((pos, i) => <circle key={`${id}-dot-${i}`} cx={pos.x} cy={pos.y} r={4.5} fill={dotColor} opacity={0.7} data-id="element-83" />)}
    </g>;
}